import {
	ACCESS_MODE,
	Advertisement,
	AdvertisementAsset,
	AdvertisementAssetType,
	BoxLayoutProperties,
	Client,
	DeviceType,
	FileResult,
	Template,
} from '@horasys/bellplus/common/types';

import {
	AdvertisementAction,
	ADVERTISEMENT_ASSET_UPLOAD_COMPLETE,
	DELETE_ADVERTISEMENT_ASSET,
	GENERATE_ADVERTISEMENT_ASSET,
	GET_ADVERTISEMENT_BY_ID,
	GET_ADVERTISEMENT_DATA_LIST,
	GET_ADVERTISEMENT_DATA_LIST_BY_CLIENT_ID,
	RESET_ADVERTISEMENT_FORM,
	UPDATE_ADVERTISEMENT_FORM,
	UPDATE_ADVERTISEMENT_MODE,
} from 'store/types/advertisement';
import { convertToAdvertisementAssetFromFileResult } from 'views/Common/typesUtils';

const emptyDeviceType: DeviceType = {
	id: 0,
	deviceType: '',
	width: 0,
	height: 0,
	qrScannerEnabled: false,
};
const emptyAdvertisement: Advertisement = {
	id: 0,
	name: '',
	client: { name: '', companyName: '' } as Client,
	template: { name: '' } as Template,
	deviceType: emptyDeviceType,
	templateAssets: [],
	duration: 0,
	noOfSlotsRequired: 0,
	status: '',
};
export interface AdvertisementStore {
	list: Array<any>;
	mode: ACCESS_MODE;
	selectedId: number;
	totalElements: number;
	advertisement: Advertisement;
	clientAdvertisements: Array<Advertisement>;
}
const initialState: AdvertisementStore = {
	list: [],
	mode: 'CREATE',
	selectedId: 0,
	totalElements: 0,
	advertisement: emptyAdvertisement,
	clientAdvertisements: [],
};
export const advertisementReducer = (
	state = initialState,
	action: AdvertisementAction,
): AdvertisementStore => {
	switch (action.type) {
		case GET_ADVERTISEMENT_DATA_LIST: {
			return {
				...state,
				list: action.payload.list,
				totalElements: action.payload.totalElements,
			};
		}

		case GET_ADVERTISEMENT_DATA_LIST_BY_CLIENT_ID: {
			return {
				...state,
				clientAdvertisements: action.payload,
			};
		}
		case DELETE_ADVERTISEMENT_ASSET: {
			return {
				...state,
				advertisement: {
					...state.advertisement,
					templateAssets: [
						...getUpdatedTemplateAssets(
							state.advertisement.templateAssets,
							action.payload,
						),
					],
				},
			};
		}

		case ADVERTISEMENT_ASSET_UPLOAD_COMPLETE: {
			return {
				...state,
				advertisement: {
					...state.advertisement,
					templateAssets: [
						...handleAdvertisementAssetUploaded(
							state.advertisement.templateAssets,
							action.payload.fileResult,
							action.payload.advertisementAsset,
							action.payload.type,
							action.payload.index,
						),
					],
				},
			};
		}

		case UPDATE_ADVERTISEMENT_MODE: {
			if (action.payload.mode !== 'CREATE') {
				return {
					...state,
					mode: action.payload.mode,
					selectedId: action.payload.selectedId,
				};
			} else {
				return {
					...state,
					advertisement: emptyAdvertisement,
					mode: action.payload.mode,
				};
			}
		}

		case GET_ADVERTISEMENT_BY_ID: {
			return {
				...state,
				advertisement: {
					...action.payload,
				},
			};
		}

		case RESET_ADVERTISEMENT_FORM: {
			return {
				...state,
				advertisement: {
					...emptyAdvertisement,
				},
			};
		}

		case GENERATE_ADVERTISEMENT_ASSET: {
			return {
				...state,
				advertisement: {
					...state.advertisement,
					templateAssets: generateAdvertisementAssets(action.payload),
				},
			};
		}

		case UPDATE_ADVERTISEMENT_FORM: {
			return {
				...state,
				advertisement: {
					...state.advertisement,
					...action.payload,
				},
			};
		}

		default: {
			return {
				...state,
			};
		}
	}
};

const resetAdvertisementAsset = (
	advertisementAsset: AdvertisementAsset,
): AdvertisementAsset => {
	const resetedAdvertisementAsset = { ...advertisementAsset };
	resetedAdvertisementAsset.visible = false;
	resetedAdvertisementAsset.progress = 0;
	resetedAdvertisementAsset.originalFileName = '';
	resetedAdvertisementAsset.uploadstatus = '';
	resetedAdvertisementAsset.url = '';
	resetedAdvertisementAsset.duration = 0;
	resetedAdvertisementAsset.id = 0;
	return resetedAdvertisementAsset;
};
const getUpdatedTemplateAssets = (
	advertisementAssets: Array<AdvertisementAsset>,
	delAdvertisementAsset: AdvertisementAsset,
): Array<AdvertisementAsset> => {
	let updatedAdvertisementAssets: Array<AdvertisementAsset> = [];
	const updatedAdvertisementAsset: AdvertisementAsset = resetAdvertisementAsset(
		delAdvertisementAsset,
	);

	switch (delAdvertisementAsset.type) {
		case 'video': {
			updatedAdvertisementAssets = advertisementAssets.map(
				(advertisementAsset: AdvertisementAsset) => {
					if (
						advertisementAsset.templateAsset &&
						delAdvertisementAsset.templateAsset
					) {
						if (
							advertisementAsset.templateAsset.id ===
							delAdvertisementAsset.templateAsset.id
						) {
							if (delAdvertisementAsset.thumbnailAsset) {
								updatedAdvertisementAsset.thumbnailAsset =
									delAdvertisementAsset?.thumbnailAsset;
							}
							if (delAdvertisementAsset.bufferAsset) {
								updatedAdvertisementAsset.bufferAsset =
									delAdvertisementAsset?.bufferAsset;
							}
							return updatedAdvertisementAsset;
						} else {
							return advertisementAsset;
						}
					} else {
						return advertisementAsset;
					}
				},
			);
			return updatedAdvertisementAssets;
		}
		case 'image': {
			updatedAdvertisementAssets = advertisementAssets.map(
				(advertisementAsset: AdvertisementAsset) => {
					if (
						advertisementAsset.templateAsset &&
						delAdvertisementAsset.templateAsset
					) {
						if (
							advertisementAsset.templateAsset.id ===
							delAdvertisementAsset.templateAsset.id
						) {
							return updatedAdvertisementAsset;
						} else {
							return advertisementAsset;
						}
					} else {
						return advertisementAsset;
					}
				},
			);
			return updatedAdvertisementAssets;
		}

		case 'bufferimage':
		case 'thumbnail': {
			updatedAdvertisementAssets = advertisementAssets.map(
				(advertisementAsset: AdvertisementAsset) => {
					if (advertisementAsset.templateAsset) {
						if (
							advertisementAsset.templateAsset.id ===
							delAdvertisementAsset.parentAssetId
						) {
							if (delAdvertisementAsset.type === 'bufferimage') {
								advertisementAsset.bufferAsset = updatedAdvertisementAsset;
							} else if (delAdvertisementAsset.type === 'thumbnail') {
								advertisementAsset.thumbnailAsset = updatedAdvertisementAsset;
							}
							return advertisementAsset;
						} else {
							return advertisementAsset;
						}
					} else {
						return advertisementAsset;
					}
				},
			);
			return updatedAdvertisementAssets;
		}

		case 'multipleImages': {
			const parentAdvertisementAssets: Array<AdvertisementAsset> = advertisementAssets.filter(
				(advertisementAsset: AdvertisementAsset) => {
					if (advertisementAsset.templateAsset) {
						return (
							advertisementAsset.templateAsset.id ===
							delAdvertisementAsset.parentAssetId
						);
					}
					return false;
				},
			);

			if (parentAdvertisementAssets && parentAdvertisementAssets.length) {
				if (parentAdvertisementAssets[0].children) {
					const updatedChildren: Array<AdvertisementAsset> = parentAdvertisementAssets[0].children.filter(
						(multipleImageAdvertisementAsset: AdvertisementAsset) => {
							return (
								multipleImageAdvertisementAsset.id !==
								delAdvertisementAsset.id
							);
						},
					);
					parentAdvertisementAssets[0].children = [];
					parentAdvertisementAssets[0].children = [...updatedChildren];
					if (
						parentAdvertisementAssets[0].templateAsset &&
						parentAdvertisementAssets[0].templateAsset?.multipleImageCount >
						parentAdvertisementAssets[0].children.length
					)
						handleAddImage(parentAdvertisementAssets[0]);
				}

				updatedAdvertisementAssets = advertisementAssets.map(
					(advertisementAsset: AdvertisementAsset) => {
						if (
							advertisementAsset.templateAsset &&
							parentAdvertisementAssets[0].templateAsset
						) {
							if (
								advertisementAsset.templateAsset.id ===
								parentAdvertisementAssets[0].templateAsset.id
							) {
								return parentAdvertisementAssets[0];
							}
						}
						return advertisementAsset;
					},
				);
				return updatedAdvertisementAssets;
			}
			return advertisementAssets;
		}

		default:
			return advertisementAssets;
	}
};

export const handleAddImage = (advertisementAsset: AdvertisementAsset): AdvertisementAsset => {
	if (advertisementAsset.templateAsset) {
		const newAssetObj: AdvertisementAsset = { ...advertisementAsset };
		const childrenAsset: AdvertisementAsset = {} as AdvertisementAsset;
		childrenAsset.type = 'multipleImages';
		childrenAsset.originalFileName = '';
		childrenAsset.uploadstatus = '';
		childrenAsset.url = '';
		childrenAsset.duration = 0;
		childrenAsset.id = 0;
		childrenAsset.parentAssetId = advertisementAsset.templateAsset.id;

		if (newAssetObj.children) {
			newAssetObj.children.push(childrenAsset);
		} else {
			newAssetObj.children = [];
			newAssetObj.children.push(childrenAsset);
		}
		return newAssetObj;
	}
	return advertisementAsset;
};

const generateAdvertisementAssets = (template: Template) => {
	const advertisementAssets: Array<AdvertisementAsset> = [];
	template.assets.forEach((asset: BoxLayoutProperties) => {
		const advertisementAsset: AdvertisementAsset = {} as AdvertisementAsset;
		advertisementAsset.type = asset.type;
		advertisementAsset.isPrimary = asset.isPrimary;
		advertisementAsset.visible = false;
		advertisementAsset.progress = 0;
		advertisementAsset.originalFileName = '';
		advertisementAsset.uploadstatus = '';
		advertisementAsset.url = '';
		advertisementAsset.duration = 0;
		advertisementAsset.id = 0;
		advertisementAsset.templateAsset = asset;

		advertisementAssets.push(advertisementAsset);
		if (advertisementAsset.type === 'video') {
			const thumbnailAsset: AdvertisementAsset = {} as AdvertisementAsset;
			thumbnailAsset.type = 'thumbnail';
			thumbnailAsset.originalFileName = '';
			thumbnailAsset.uploadstatus = '';
			thumbnailAsset.url = '';
			thumbnailAsset.duration = 0;
			thumbnailAsset.id = 0;
			thumbnailAsset.parentAssetId = asset.id;

			const bufferImage: AdvertisementAsset = {} as AdvertisementAsset;
			bufferImage.type = 'bufferimage';
			bufferImage.originalFileName = '';
			bufferImage.uploadstatus = '';
			bufferImage.url = '';
			bufferImage.duration = 0;
			bufferImage.id = 0;
			bufferImage.parentAssetId = asset.id;

			advertisementAsset.thumbnailAsset = thumbnailAsset;
			advertisementAsset.bufferAsset = bufferImage;
		} else if (advertisementAsset.type === 'multipleImages') {
			const childrenAsset: AdvertisementAsset = {} as AdvertisementAsset;
			childrenAsset.type = 'multipleImages';
			childrenAsset.originalFileName = '';
			childrenAsset.uploadstatus = '';
			childrenAsset.url = '';
			childrenAsset.duration = 0;
			childrenAsset.id = 0;
			childrenAsset.parentAssetId = asset.id;

			advertisementAsset.children = [];
			advertisementAsset.children.push(childrenAsset);
		}
	});
	return [...advertisementAssets];
};

const handleAdvertisementAssetUploaded = (
	templateAssets: Array<AdvertisementAsset>,
	fileResult: FileResult,
	advertisementAsset: AdvertisementAsset,
	type: AdvertisementAssetType,
	selectedIndex: number,
) => {
	let newAssetObj: AdvertisementAsset = { ...advertisementAsset };
	switch (type) {
		case 'video':
		case 'image':
			newAssetObj.id = fileResult.id;
			newAssetObj.duration = fileResult.duration;
			newAssetObj.originalFileName = fileResult.fileName;
			newAssetObj.url = fileResult.url;
			newAssetObj.uploadstatus = fileResult.uploadStatus;
			newAssetObj.source = fileResult.source;
			break;
		case 'thumbnail':
			newAssetObj.thumbnailAsset = {} as AdvertisementAsset;
			newAssetObj.thumbnailAsset.id = fileResult.id;
			newAssetObj.thumbnailAsset.duration = fileResult.duration;
			newAssetObj.thumbnailAsset.originalFileName = fileResult.fileName;
			newAssetObj.thumbnailAsset.url = fileResult.url;
			newAssetObj.thumbnailAsset.uploadstatus = fileResult.uploadStatus;
			newAssetObj.thumbnailAsset.type = 'thumbnail';
			newAssetObj.thumbnailAsset.parentAssetId =
				advertisementAsset.templateAsset?.id;
			newAssetObj.thumbnailAsset.source = fileResult.source;
			break;
		case 'bufferimage':
			newAssetObj.bufferAsset = {} as AdvertisementAsset;
			newAssetObj.bufferAsset.id = fileResult.id;
			newAssetObj.bufferAsset.duration = fileResult.duration;
			newAssetObj.bufferAsset.originalFileName = fileResult.fileName;
			newAssetObj.bufferAsset.url = fileResult.url;
			newAssetObj.bufferAsset.uploadstatus = fileResult.uploadStatus;
			newAssetObj.bufferAsset.type = 'bufferimage';
			newAssetObj.bufferAsset.parentAssetId = advertisementAsset.templateAsset?.id;
			newAssetObj.bufferAsset.source = fileResult.source;
			break;
		case 'multipleImages':
			{
				const newChildren = newAssetObj.children?.map(
					(childAdvertisementAsset: AdvertisementAsset, index: number) => {
						if (selectedIndex === index) {
							return convertToAdvertisementAssetFromFileResult(
								fileResult,
								childAdvertisementAsset,
							);
						} else {
							return childAdvertisementAsset;
						}
					},
				);
				if (newChildren) {
					newAssetObj.children = [...newChildren];
					if (
						newAssetObj.templateAsset &&
						newAssetObj.templateAsset?.multipleImageCount
					)
						if (
							newAssetObj.templateAsset?.multipleImageCount >
							newAssetObj.children.length
						)
							newAssetObj = handleAddImage(newAssetObj);
				}
			}
			break;
		default:
			break;
	}
	const updatedAdAssets: Array<AdvertisementAsset> = templateAssets.map(
		(asset: AdvertisementAsset) => {
			if (asset.templateAsset && newAssetObj.templateAsset) {
				if (asset.templateAsset.id === newAssetObj.templateAsset.id) {
					return newAssetObj;
				} else {
					return asset;
				}
			} else {
				return asset;
			}
		},
	);

	return [...updatedAdAssets];
};
